/**
 * What the AI is allowed to learn from dry-running a pre-request script.
 *
 * `generate_tests` can run its script against a STORED response and report
 * pass/fail, because by then a response exists. A pre-request script has no
 * equivalent — it runs before anything is sent. The useful equivalent is a DRY
 * RUN: does it compile, does it throw, and which variables would it set. That
 * is exactly the class of mistake (syntax errors, wrong pm.* surface, reading
 * pm.response pre-request) the user wants the model to stop making.
 *
 * ## Where the guarantee actually lives
 *
 * It lives in ONE place, and it is not this file: on the AI path every
 * variable marked `secret: true` is passed to the sandbox as WITHHELD,
 * `scriptAccess: true` grant or not (`allSecretsWithheld` in
 * `scriptVariables.js`; the values are then deleted by `buildSandboxPayload`
 * before the worker is ever posted to). No secret value is inside the sandbox
 * during a dry run, so there is no secret for a script to encode into a
 * variable NAME, a `pm.test` label, an error message, or anything else this
 * module projects. The covert-channel class is removed rather than filtered.
 *
 * This was not always true, and the previous version of this docstring claimed
 * a layered guarantee it did not have. The honest description of the redaction
 * below is: **a backstop for the non-hostile case, not a containment
 * boundary.** It is a substring filter, and a substring filter loses to any
 * transformation a hostile script cares to apply —
 * `pm.environment.get('K').split('').join('.')` and `.slice(0, 12)` both walk
 * straight through it. Nothing here should be read as protecting against a
 * script that is trying to leak. What protects against that is the withholding
 * above.
 *
 * ## What is projected, and why redaction still runs
 *
 * Names, never values. A pre-request script's whole job is usually to compute
 * a credential and stash it with `pm.environment.set()`, so the summary
 * carries key names and nothing else. But a NAME is attacker-chosen too —
 * `pm.environment.set(pm.environment.get('SIGNING_KEY'), '1')` puts the value
 * in the name position — so redaction is applied to names, `pm.test` labels
 * and error strings alike. Defence in depth on top of the withholding, so the
 * guarantee does not rest on a single mechanism.
 *
 * Applying the mutations is deliberately NOT this module's job and is not done
 * anywhere on the AI path. See `aiGeneratePreRequestScript` in useAiActions.
 */

/**
 * ## Shared with generate_tests
 *
 * `secretVariableValues` and `redactSecretValues` below are NOT pre-request
 * specific and are used by `aiGenerateTests` too (see `useAiActions.js`). They
 * live here because this is where the honest description of what a substring
 * filter can and cannot do is written down, and duplicating the filter is
 * exactly how the two AI tools ended up with two different secret policies in
 * the first place. `summarizePreRequestDryRun` is the only dry-run-specific
 * export.
 */

/** Placeholder left where a secret value was scrubbed out. */
export const REDACTION = '<redacted>';

/**
 * Values of every variable the user marked `secret: true`, across all scopes.
 *
 * Includes `scriptAccess: true` grants: on the AI path those are withheld from
 * the sandbox too, so this list is a backstop covering the host-side error
 * path (an exception thrown while BUILDING the run, before the sandbox
 * exists), not the sandbox itself.
 *
 * There is deliberately no minimum length any more. The old floor of 3
 * characters was an attempt to keep a short, prose-like "secret" from shredding
 * an unrelated message, and it failed at both ends: `pin=ab` was never redacted
 * at all, while a secret whose value happened to be `not` still turned
 * `undefinedFunctionCall is not defined` into
 * `undefinedFunctionCall is <redacted> defined`. Those two are jointly
 * unsatisfiable by any substring filter — `ab` and `not` are indistinguishable
 * as strings — which is the clearest possible argument for not relying on the
 * filter. Given that, the floor is removed and the leak direction is closed:
 * a scrubbed message is visibly scrubbed and the model is told (in the tool
 * description) that `<redacted>` may stand in for an ordinary word, whereas a
 * leaked credential is silent.
 */
export function secretVariableValues(scopes = {}) {
  const values = new Set();
  const sources = [
    scopes.globalVariables,
    scopes.workspaceVariables,
    scopes.environmentVariables,
    scopes.collectionVariables,
    scopes.folderVariables,
  ];
  for (const source of sources) {
    if (!Array.isArray(source)) continue;
    for (const v of source) {
      if (v?.secret !== true) continue;
      const value = String(v.value ?? '');
      if (value !== '') values.add(value);
    }
  }
  return [...values];
}

/** Replace every occurrence of each secret value in `text` with REDACTION. */
export function redactSecretValues(text, secretValues = []) {
  if (typeof text !== 'string' || text === '') return text;
  let out = text;
  // Longest first, so a secret that contains another is not half-replaced.
  for (const value of [...secretValues].sort((a, b) => b.length - a.length)) {
    if (!value) continue;
    out = out.split(value).join(REDACTION);
  }
  return out;
}

/**
 * Project a sandbox outcome into the dry-run report handed to the model.
 *
 * @param {{variables?: object, envMutations?: object, errors?: string[], tests?: object[]}} outcome
 * @param {{secretValues?: string[]}} options
 */
export function summarizePreRequestDryRun(outcome, { secretValues = [] } = {}) {
  const envMutations = outcome?.envMutations || {};
  const localMutations = outcome?.variables || {};
  const scrub = (text) => redactSecretValues(String(text ?? ''), secretValues);

  const wouldSet = [];
  const wouldUnset = [];
  for (const [key, value] of Object.entries(envMutations)) {
    // The sandbox encodes `environment.unset()` as an explicit null.
    (value === null ? wouldUnset : wouldSet).push(scrub(key));
  }

  const firstError = (outcome?.errors || []).find((e) => typeof e === 'string' && e) || null;

  // `pm.test` swallows a throw into `outcome.tests` instead of `outcome.errors`
  // — so a summary that reads only `errors` reports
  // `pm.test('x', () => { throw })` as ok:true. The description tells the model
  // pm.test is pointless pre-request, but Postman priors are strong and it uses
  // it anyway, and a green dry run on a script that threw is the one thing this
  // tool must never say.
  const failedTests = (outcome?.tests || [])
    .filter((t) => t && t.passed === false)
    .map((t) => ({ name: scrub(t.name), error: t.error ? scrub(t.error) : null }));

  return {
    ok: !firstError && failedTests.length === 0,
    error: firstError === null ? null : scrub(firstError),
    failed_tests: failedTests,
    // Names only — see the docstring. Never spread envMutations/variables here:
    // a name is script-chosen and can be a value the script computed.
    would_set_environment_variables: wouldSet.sort(),
    would_unset_environment_variables: wouldUnset.sort(),
    would_set_local_variables: Object.keys(localMutations).map(scrub).sort(),
  };
}
